import { Item } from '@/Engine/Item';
import { IV3D } from '@/Interfaces/v3d';
import { Door } from '@/Engine/Door';
import { iPartProps, Part } from '@/Engine/Part';
import { Object3D, Vector3 } from 'three';
import { modelMeta } from '@/Constants/modelMeta';
import { iCaseComponentsResultItem, iDrawerSeparatorResultItem, tBlenderCaseModel, tCaseComponentType, tCaseModel } from '@/Interfaces/caseComponentsLib';
import { iStoreCaseConfiguration } from '@/Interfaces/storeCaseConfiguration';
import { caseComponentsLib } from '@/Constants/caseComponentsLib/caseComponentsLib';
import { getPositionXFromHoles, getPositionYFromHoles } from '@/libs/getPositionsFromHoles';
import { $ } from '@/Engine/state';
import { markAllCollision, noMarkAllCollision } from '@/Engine/eventHandlers/libs/markCollision';
import { createCube } from '@/libs/createCube';
import { faceNodeNameRGB, frameNodeNameRGB } from '@/Constants/colors';
import { itemBoxHolder } from '@/Constants/itemBoxHolder';
import { Drawer } from '@/Engine/Drawer';
import { shelfCNCHard } from '@/Engine/shelfCNCHard';
import { useStoreCaseConfiguration } from '@/Store/storeCaseConfiguration';
import { v3dMesh } from '@/Interfaces/common';

declare let v3d: IV3D;


export class ToolChest extends Item {
    doorLeft: Door | null = null;
    doorRight: Door | null = null;
    //separator и separatorT могут быть в количестве 0 или 1шт
    separator: Array<Part> = [];
    separatorT: Array<Part> = [];
    //
    shelfCNC: Array<Part> = [];
    shelf: Array<Part> = [];
    shield: Array<Part> = [];
    drawer: Array<Drawer> = [];
    accessory: Array<Part> = [];
    shelfCNCHard: Array<shelfCNCHard> = [];

    blenderName!: tBlenderCaseModel | undefined;
    colorFrame!: string;
    colorFace!: string;
    width?: number;
    height?: number;
    fileName?: string;
    selectedCaseModel!: tCaseModel;
    selectedCasePresetId!: string | undefined;
    drawerSeparators?: Array<iDrawerSeparatorResultItem>;
    expandedDrawersPrototypeId?: Array<string>;

    models: Object3D[] = [];
    cubeNameModels: string[] = [];

    constructor(props: iStoreCaseConfiguration, model: Object3D) {
        const meta = modelMeta.find(item => item.name === props.blenderName);
        if (!meta) {
            console.error('не найдено meta для', props.blenderName);
            return;
        }
        super({ id:'', ...meta, model: model });
        this.fileName = meta.fileName;
        this.width = meta.width;
        this.height = meta.height;

        this.update(props);

        this.setDoor(true, this.createDoor(true));
        this.setDoor(false, this.createDoor(false));

        this.setColors(props.colorFrame, props.colorFace);
        this.switchDoors(props.isCloseDoors);
    }

    get parts() {
        return [
            ...this.separator,
            ...this.separatorT,
            ...this.shelfCNC,
            ...this.shelf,
            ...this.shield,
            ...this.drawer,
            ...this.accessory,
            ...this.shelfCNCHard,
        ];
    }

    setColorBaseToolChest(colorFrame: string, colorFace: string) {
        this.colorFrame = colorFrame;
        this.colorFace = colorFace;
        this.setColor(frameNodeNameRGB, this.colorFrame);
        this.setColor(faceNodeNameRGB, this.colorFace);
        this.doorLeft?.setColor(faceNodeNameRGB, this.colorFace);
        this.doorRight?.setColor(faceNodeNameRGB, this.colorFace);
    }
    setColors(colorFrame: string, colorFace: string) {
        this.setColorBaseToolChest(colorFrame, colorFace);
        this.parts?.forEach((part: Part) => {
            part.setColor(frameNodeNameRGB, this.colorFrame);
            part.setColor(faceNodeNameRGB, this.colorFace);
        });
    }

    changeDrawersModels(props: iStoreCaseConfiguration) {
        this.expandedDrawersPrototypeId = props.expandedDrawersPrototypeId;
        this.drawerSeparators = props.drawerSeparators;
        this.drawer.forEach(drawer => drawer.visibleSeparators());
    }
    update(props: iStoreCaseConfiguration) {
        this.blenderName = props.blenderName;
        let posY, posX;
        let part: Part|null;
        const ids = props.caseItems.map(x => x.id);
        const idsToRemove: Array<string> = [];
        this.raw.caseItems?.forEach(x => {
            if (ids.includes(x.id)) return;
            idsToRemove.push(x.id);
        });

        //удалить все комплектующие которых нет в props
        idsToRemove.forEach(id => {
            part = this.getComponentById(id);
            this.removeComponent(part);
        });

        //добавить только те комплектующие которые есть в props но нет в ToolChest (this)
        props.caseItems.forEach((item: iCaseComponentsResultItem) => {
            if (this.getComponentById(item.id) || item.isDefaultHolder) {
                return;
            }

            const part = this.createPartItem(item);
            if (!part) return;

            //устанавливаем position и scale для part
            part._isLeft = item.isLeft;
            posY = getPositionYFromHoles(item, this.blenderName);
            posX = getPositionXFromHoles(item, this.blenderName);
            part.position = new Vector3(posX, posY, 0);
            this.addComponent(part);
        });

        this.setColors(props.colorFrame, props.colorFace);
        this.switchDoors(props.isCloseDoors);
        this.changeDrawersModels(props);
    }
    addSectionsCNCToGroup() {
        //Только для чпу: обновить стор для появления в спецификации кронштейнов
        //так как добавляю его как child в чпу => исходно модели нет
        if (!$.isUpdateStore) return;
        $.isUpdateStore = false;
        if (!this.shelfCNCHard.length) return;

        this.shelfCNCHard.filter(section => {
            //if (section.getNumberSection() === 1) return;
            const s1 = this.shelfCNCHard.find(x => x.getNumberSection() === 1 && section.idGroup === x.idGroup);
            if (!s1) return;
            if (s1.isChild(section)) return;
            section?.model.userData?.parent.join(s1.model);
        });

        useStoreCaseConfiguration().$patch({ caseItems: $.toolChest?.raw?.caseItems });
    }

    switchDoors(isCloseDoors: boolean) {
        $.continueRender();
        if (isCloseDoors) {
            this.doorLeft?.close();
            this.doorRight?.close();
            return;
        }
        this.doorLeft?.openHalf();
        this.doorRight?.openHalf();
    }

    getComponentById(id: string) {
        return this.parts.find(x => x.id === id) ?? null;
    }
    addComponent(part: Part|Drawer|shelfCNCHard) {
        this.model.add(part.model);
        this[part.key]?.push(part);
    }

    getDrawersByPrototypeId(id: string) {
        return this.drawer.filter(drawer => drawer.prototypeId === id);
    }
    getDrawerId(id: string) {
        return this.getDrawersByPrototypeId(id)?.length;
    }

    recalculateGlobalId(id: string) {
        this.getDrawersByPrototypeId(id).forEach((drawer: Drawer) => {
            drawer.globalId -= 1;
        });
    }

    recalculateMaxCountForSeparators(partId: string) {
        const separator = this.drawerSeparators?.find(x => x.drawerPrototypeId === partId);
        if (!separator) return;
        if (separator.count === 0) {
            this.drawerSeparators = this.drawerSeparators?.filter(x => x.prototypeId !== partId);
            return;
        }
        separator.count -= 1;
    }
    createPartFromData(part) {
        if (!part || !part?.raw) return null;
        const data = { ...part.raw };
        data.id = crypto.randomUUID();
        const id = part.idGroup ?? $.toolChest?.shelfCNCHard.filter(x => x.getNumberSection() === 1)?.length;
        data.idGroup = id + 1;
        return $.toolChest?.createPartItem(data);
    }

    copy(part) {
        const item = this.createPartFromData(part);
        if (!item) return null;
        if (item.key !== 'shelfCNCHard') return item;

        // TODO копирование чпу хард полностью группы не рабоатет, только 1ой секции!
        // part?.model.children?.forEach((child) => {
        //     if (!child.isMesh || child?.name?.includes('_s1')) return;
        //     item?.model.add(child.clone() as v3dMesh);
        //     const chhh = caseComponentsLib.find(x => x.blenderName === child.userData.name);
        //     if (!chhh) return;
        //     const itemCh = $.toolChest?.createPartItem({
        //         id: child.uuid,
        //         prototypeId: chhh?.id,
        //         indexPositionY: child.parent?.userData.parent.indexPositionY,
        //         isLeft: child.parent?.userData.parent.isLeft,
        //         idGroup: item.raw.idGroup,
        //     }) as shelfCNCHard;
        //     if (!itemCh.parent) return;
        //     this.shelfCNCHard.push(itemCh as shelfCNCHard);
        // });
        // console.log('DCADSVFAVFD',part, item);
        return item;
    }

    removeComponent(part?: Part|Drawer|shelfCNCHard|null) {
        if (!part) return;
        const children = part?.model?.children?.map(child => child.userData?.parent);
        this[part.key] = this[part.key]?.filter(x => x !== part && !children.includes(x));
        part.destroy();

        if (part.key === 'drawer') {
            this.recalculateGlobalId(part.prototypeId);
            this.recalculateMaxCountForSeparators(part.prototypeId);
        }
    }
    createDoor(isLeft: boolean) {
        const doorName: string = (isLeft ? 'dl_' :  'dr_') + this.blenderName;
        const metaItemDoor = modelMeta.find(x => x.name === doorName);
        const modelDoor = this._model?.children?.find(item => item.name === doorName);
        if (metaItemDoor && modelDoor) {
            return new Door({ id:'', ...metaItemDoor, model: modelDoor }, this.colorFace, isLeft);
        }
    }
    createHolder(parentPart: Part) {
        const holder = itemBoxHolder;
        const part = this.createPartItem(holder);
        if (!part) return;
        parentPart.model.add(part.model);
        part.model.position.set(0, -0.07, 0);
    }


    setDoor(isLeft: boolean, door?: Door) {
        if (!door) return;
        if (isLeft) {
            this.doorLeft = door;
            this.model.add(this.doorLeft.model);
            return;
        }
        this.doorRight = door;
        this.model.add(this.doorRight.model);
    }

    replaceBoxHolder(model) {
        //заменяем дочернюю модель: держатель ящика
        const metaItem = modelMeta.find(x => x.name === model?.name);
        const offset = metaItem?.offset;
        let parent;
        this.accessory
            .filter(x => x.isPlasticBoxWithParentShield())
            .forEach(x =>
                x.model.children
                    .filter(x => {
                        parent = x.userData?.parent;
                        if (!parent?.isDefaultHolder) return;
                        parent?.changeModel(model);
                        if (!offset) return;
                        parent?._model.position.set(offset[0], offset[1], offset[2]);
                    }),
            );

    }
    replaceChpuHolder(model) {
        //заменяем дочернюю модель: кронштейн для чпу
        const metaItem = modelMeta.find(x => x.name === model?.name);
        const offset = metaItem?.offset;
        let parent;
        this.shelfCNCHard.forEach(item =>
            item.model.children
                .filter(x => x.userData?.parent?.model?.name === 'shelf_chpu_hard')
                .filter(x => {
                    parent = x.userData?.parent;
                    parent?.changeModel(model);
                    if (!offset) return;
                    parent?._model?.position.set(offset[0], offset[1], offset[2]);
            }),
        );
    }

    async replaceModel(blenderName: string) {
        this.parts
            .filter(x => x.model.name === blenderName);
        const model = await $.modelCache?.getOrUploadModelByName(blenderName);
        if (!model) return;

        const metaItem = modelMeta.find(x => x.name === model?.name);
        // ищем среди children модели держателей для ящика, чтоб заменить на загруженные
        if (blenderName === 'plastic_box_holder_200_SPBH') {
            this.replaceBoxHolder(model);
        }
        if (blenderName === 'shelf_chpu_hard') {
            this.replaceChpuHolder(model);
        }

        for (const part of this.parts) {
            if (part.model.name !== model?.name) continue;
            part.changeModel(model);
            if (metaItem) {
                part._offset = metaItem.offset;
                part._model.position.set(part._offset[0], part._offset[1], part._offset[2]);
            }
        }
        noMarkAllCollision();
        markAllCollision();
        this.setColors(this.colorFrame, this.colorFace);
        $.render();
    }

    //подмена не загруженных моделей на загруженные
    async replaceModels() {
        if (!this.cubeNameModels || !this.cubeNameModels.length) return;
        for (const name of this.cubeNameModels) {
            await this.replaceModel(name);
        }
        this.cubeNameModels = [];
    }

    //загрузка всех комплектующих из fileName
    async uploadModels(fileName: string) {
        // const nameComponents = getComponentsByFileName(fileName);
        // if (!nameComponents) return;
        // for (const modelName of nameComponents) {
        //     if (!modelCache.get(modelName)) {
        //         await uploadModel(modelName) as Object3D;
        //     }
        // }
    }

    get raw(): iStoreCaseConfiguration{
        const caseItems = [
            ...this.shelfCNC.map((item) => item.raw),
            ...this.shelf.map((item) => item.raw),
            ...this.shield.map((item) => item.raw),
            ...this.drawer.map((item) => item.raw),
            ...this.accessory.map((item) => item.raw),
            ...this.shelfCNCHard.map((item) => item.raw),
            ...this.separatorT.map((item) => item.raw),
            ...this.separator.map((item) => item.raw),
        ];
        const plasticBoxes = this.accessory.filter((item) => item.model.name === 'plastic_box_400x230x150');
        let child;
        plasticBoxes.forEach(box => {
            child = box.model.children.find(item => item?.userData.parent?.isDefaultHolder);
            if (!child) return;
            caseItems.push(child?.userData.parent?.raw);
        });
        this.shelfCNCHard
            .map(x => x.model.children)
            .flat()
            .filter((item) => {
            if (item.name === 'shelf_chpu_hard') {
                caseItems.push(item.userData.parent.raw);
            }
        });

        return {
            blenderName: this.blenderName,
            colorFrame: this.colorFrame,
            colorFace: this.colorFace,
            selectedCaseModel: this.selectedCaseModel,
            selectedCasePresetId: this.selectedCasePresetId,
            drawerSeparators: this.drawerSeparators ?? [],
            expandedDrawersPrototypeId: this.expandedDrawersPrototypeId ?? [],
            caseItems: caseItems,
            isCloseDoors: (this.doorRight?.isCloseDoors && this.doorLeft?.isCloseDoors) ?? false,
            //TODO
            isDecoration: false,
        };
    }



    createPartItem(item: iCaseComponentsResultItem) {
        const caseComponent = caseComponentsLib.find(component => component.id === item.prototypeId);
        if (!caseComponent) {
            console.error('Не найдено caseComponent');
            return null;
        }
        const metaItem = modelMeta.find(x => x.name === caseComponent.blenderName);
        if (!metaItem) {
            console.warn('не найдено meta для', caseComponent.blenderName);
            return null;
        }

        if (this.blenderName?.includes('hard') &&
            caseComponent.type === 'accessory' && metaItem.box) {
            metaItem.offset[2] = 0.03;
            metaItem.box.offset[2] = 0.03;
        }

        let model: v3dMesh|undefined = $.modelCache?.getModelByName(metaItem.name);
        let offset = metaItem.offset;
        let isCube = false;
        if (!model && metaItem) {
            model = createCube(metaItem);
            isCube = true;
            const posX = metaItem.box?.isSmall ? metaItem.box?.width / 2 : 0;
            if (metaItem?.box) {
                offset = [
                    posX + metaItem.box.offset[0],
                    metaItem.box.height / 2 + metaItem.box.offset[1],
                    metaItem.box.offset[2] + metaItem.box.depth / 2,
                ];
            }
        }

        const partProps: iPartProps = {
            key: caseComponent.type as Exclude<tCaseComponentType, 'other'>,
            id: item.id,
            indexPositionY: item?.indexPositionY === undefined ? undefined : item?.indexPositionY,
            indexPositionX: item?.indexPositionX === undefined ? undefined : item?.indexPositionX,
            isLeft: item.isLeft,
            width: metaItem.width as number,
            height: metaItem.height as number,
            depth: metaItem.depth as number,
            decorPosition: metaItem.decorPosition,
            scale: metaItem.scale,
            offset: offset,
            rotation: metaItem.rotation,
            model: model,
            prototypeId: item.prototypeId,
            isDefaultHolder: item.isDefaultHolder,
        };

        let part;
        switch (caseComponent.type) {
            case 'drawer':
                part = new Drawer(partProps, this.getDrawerId(item.prototypeId) + 1) as Drawer;
                break;
            case 'shelfCNCHard': {
                const idGroup = item.idGroup ?? this.shelfCNCHard.filter(x => x.getNumberSection() === 1)?.length;
                part = new shelfCNCHard(partProps, idGroup) as shelfCNCHard;
                break;
            }
            default:
                part = new Part(partProps);
        }
        if (isCube) {
            this.cubeNameModels.push(metaItem.name);
        }

        if (!part) {
            console.error('Ошибка создания part');
            return null;
        }

        return part;
    }


}
